// src/components/layouts/MainLayout.js
import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../dashboard/Sidebar';
import { useProjects } from '../../contexts/ProjectContext';

const MainLayout = () => {
  const { user, loading: authLoading, onboardingCompleted } = useAuth();
  const { isCurrentProjectOnboarded, loading: projectLoading } = useProjects();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  
  // Handle tab visibility to prevent unnecessary renders on tab switch
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
      
      // If coming back to visible tab, update initialLoadComplete
      if (document.visibilityState === 'visible') {
        setInitialLoadComplete(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Only show loading on first render, not on tab switching
  useEffect(() => {
    // If we have cached onboarding status, use it immediately
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    if (cachedStatus !== null && cachedStatus === 'true') {
      // If we know we're onboarded, don't show loading
      setInitialLoadComplete(true);
    } else {
      // Otherwise use standard logic
      const isPageReload = window.performance && 
        window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
      
      // Only delay if this is a page reload
      if (isPageReload) {
        setTimeout(() => {
          setInitialLoadComplete(true);
        }, 800);
      } else {
        // For normal navigation, don't add delay
        setInitialLoadComplete(true);
      }
    }
  }, []);

  // Handle onboarding redirect with delay
  useEffect(() => {
    // Skip redirect logic when tab is not visible
    if (!isTabVisible) return;
    
    // Use cached status if available on first render
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    const cachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
    
    // Check if onboarding is completed (either from AuthContext or localStorage)
    const isOnboardingComplete = onboardingCompleted || cachedOnboardingCompleted;
    
    // On initial load, if we have a definite "false" for onboarding status,
    // wait a bit before redirecting to prevent flash of dashboard
    if (initialLoadComplete && !isOnboardingComplete && isCurrentProjectOnboarded === false) {
      // Wait 2 seconds before deciding to redirect
      const timer = setTimeout(() => {
        // Double-check onboarding status to make sure it hasn't changed
        const currentCachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
        const currentIsOnboardingComplete = onboardingCompleted || currentCachedOnboardingCompleted;
        if (!currentIsOnboardingComplete && isCurrentProjectOnboarded === false) {
          setShouldRedirect(true);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    // If status changes to true or onboarding is marked complete, clear redirect flag
    if (isOnboardingComplete || isCurrentProjectOnboarded === true) {
      setShouldRedirect(false);
    }
  }, [isCurrentProjectOnboarded, onboardingCompleted, initialLoadComplete, isTabVisible, user]);

  // Only show loading on initial page load or when authentication is actually loading
  // Skip loading screen when tab is not visible
  if ((authLoading || projectLoading) && !initialLoadComplete && isTabVisible) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
          <div className="mt-4 text-primary-600 text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Delay redirect to onboarding until we're sure it's necessary
  // Only redirect when tab is visible
  const cachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
  const isOnboardingComplete = onboardingCompleted || cachedOnboardingCompleted;
  
  if (shouldRedirect && !isOnboardingComplete && isCurrentProjectOnboarded === false && isTabVisible) {
    // If onboarding is not complete, redirect to the onboarding flow
    return <Navigate to="/onboarding/business" replace />;
  }

  return (
    <div className="flex h-screen ">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Removed the header/top nav bar */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;